import React, { useCallback, useEffect, useRef, useState } from "react";
import fetchInvoicePaymentStatus from "../../functions/invoice/fetchInvoicePaymentStatus";
import { requestJson } from "../../service/api";
import { useUser } from "../../screens/user/context.js/userContext";
import { Button, Skeleton } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";

const POPULAR_BANK_NAMES = [
  "Khan bank",
  "State bank 3.0",
  "Xac bank",
  "Trade and Development bank",
  "Capitron bank",
  "Social Pay",
  "Bogd bank",
  "M bank",
  "Monpay",
];

const POLL_MS = 7000;

const popularRank = (name) => {
  const index = POPULAR_BANK_NAMES.findIndex(
    (label) => String(label).toLowerCase() === String(name || "").toLowerCase()
  );
  return index === -1 ? 999 : index;
};

const formatMmSs = (ms) => {
  if (ms <= 0) return "00:00";
  const t = Math.floor(ms / 1000);
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const sortBanks = (urls) =>
  [...(urls || [])].sort((a, b) => popularRank(a.name) - popularRank(b.name));

function sessionTimerHint(remainingMs) {
  if (remainingMs <= 60 * 1000) return "Удахгүй дуусна — доорх банкаа сонгоод төлнө үү.";
  if (remainingMs <= 5 * 60 * 1000) return "Түр л хүчинтэй — одоо банкаа сонгоорой.";
  return "Энэ хугацаанд доорх банкны аппаар төлнө.";
}

const INVOICE_EXPIRED_MSG =
  "Нэхэмжлэлийн хугацаа дууссан. Дахин төлөх бол төлбөр үүсгэсэн системээс шинэ нэхэмжлэл үүсгэнэ үү.";

const INVOICE_CLOSED_MSG =
  "Төлбөрийн цонх хаагдлаа. Дахин төлөх бол төлбөр үүсгэсэн системээс шинэ нэхэмжлэл авна уу.";

/** idle = анхны QR; CLOSED = төлөгдсөн, цуцлагдсан, хугацаа дууссан (invoice EXPIRED) */
const PayPhase = {
  LOADING: "loading",
  ACTIVE: "active",
  IDLE: "idle",
  CLOSED: "closed",
  ERROR: "error",
  LEGACY: "legacy",
};

const QpayBankList = ({ invoiceId, onPaid, useSessionApi = true }) => {
  const [banks, setBanks] = useState(null);
  const [phase, setPhase] = useState(PayPhase.LOADING);
  const [loading, setLoading] = useState(false);
  const [closedMessage, setClosedMessage] = useState(null);
  const [expiresAtMs, setExpiresAtMs] = useState(null);
  const [clock, setClock] = useState(() => Date.now());
  const { setPaying } = useUser();
  const fetchStartedRef = useRef(false);

  const remainingMs =
    expiresAtMs && phase === PayPhase.ACTIVE ? Math.max(0, expiresAtMs - clock) : 0;

  const applyActiveSession = useCallback((active) => {
    const urls = active?.qpay?.urls;
    if (!urls?.length) return false;
    setBanks(sortBanks(urls));
    setExpiresAtMs(new Date(active.expiresAt).getTime());
    setPhase(PayPhase.ACTIVE);
    return true;
  }, []);

  /** Main channel: POST /invoice/:id/createQpay (flag on → paymentSession + QPay). */
  const openViaCreateQpay = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    setPaying("Төлбөрийн мэдээлэл татаж байна...");
    try {
      const res = await requestJson("post", `/invoice/${invoiceId}/createQpay`);
      setPaying(null);
      const code = res.body?.code;
      if (!res.ok) {
        if (code === "INVOICE_NOT_OPEN" || code === "INVOICE_ALREADY_PAID" || code === "INVOICE_EXPIRED") {
          const st = res.body?.data?.status;
          if (st === "PAID" || st === "OVERPAID") onPaid?.();
          setClosedMessage(st === "EXPIRED" || code === "INVOICE_EXPIRED" ? INVOICE_EXPIRED_MSG : null);
          setPhase(PayPhase.CLOSED);
          setBanks([]);
          return;
        }
        setPhase(PayPhase.ERROR);
        setBanks([]);
        return;
      }
      const payload = res.body?.data?.data;
      const urls = payload?.urls;
      if (!urls?.length) {
        setPhase(PayPhase.ERROR);
        setBanks([]);
        return;
      }
      setBanks(sortBanks(urls));
      if (payload.expiresAt) {
        setExpiresAtMs(new Date(payload.expiresAt).getTime());
        setPhase(PayPhase.ACTIVE);
      } else {
        setExpiresAtMs(null);
        setPhase(PayPhase.LEGACY);
      }
    } catch {
      setPaying(null);
      setPhase(PayPhase.ERROR);
      setBanks([]);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, setPaying, onPaid]);

  const loadFromStatus = useCallback(async () => {
    const res = await fetchInvoicePaymentStatus(invoiceId);
    if (!res.ok || !res.body?.success) {
      return { autoOpenSession: true };
    }
    const d = res.body.data;
    if (d.isPaid) {
      onPaid?.();
      setPhase(PayPhase.CLOSED);
      setBanks([]);
      return { autoOpenSession: false };
    }
    if (d.status === "CANCELLED" || d.status === "OVERPAID" || d.status === "EXPIRED") {
      setClosedMessage(d.status === "EXPIRED" ? INVOICE_EXPIRED_MSG : null);
      setPhase(PayPhase.CLOSED);
      setBanks([]);
      return { autoOpenSession: false };
    }
    if (d.activeSession && applyActiveSession(d.activeSession)) {
      return { autoOpenSession: false };
    }
    setBanks([]);
    setExpiresAtMs(null);
    setClosedMessage(null);
    setPhase(PayPhase.IDLE);
    return { autoOpenSession: d.status === "CREATED" };
  }, [invoiceId, onPaid, applyActiveSession]);

  const initialLoad = useCallback(async () => {
    if (!invoiceId) return;
    fetchStartedRef.current = true;
    setPhase(PayPhase.LOADING);
    if (!useSessionApi) {
      await openViaCreateQpay();
      return;
    }
    const { autoOpenSession } = await loadFromStatus();
    if (autoOpenSession) await openViaCreateQpay();
  }, [invoiceId, useSessionApi, loadFromStatus, openViaCreateQpay]);

  useEffect(() => {
    if (invoiceId && !fetchStartedRef.current) {
      initialLoad();
    }
  }, [invoiceId, initialLoad]);

  useEffect(() => {
    if (phase !== PayPhase.ACTIVE || !expiresAtMs) return;
    const tick = setInterval(() => {
      const now = Date.now();
      setClock(now);
      if (now >= expiresAtMs) {
        setBanks([]);
        setExpiresAtMs(null);
        loadFromStatus();
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [phase, expiresAtMs, loadFromStatus]);

  useEffect(() => {
    if (phase !== PayPhase.ACTIVE || !invoiceId) return;
    const poll = async () => {
      const res = await fetchInvoicePaymentStatus(invoiceId);
      if (!res.ok || !res.body?.success) return;
      const d = res.body.data;
      if (d.isPaid) {
        onPaid?.();
        return;
      }
      if (d.status === "CANCELLED" || d.status === "EXPIRED") {
        setClosedMessage(d.status === "EXPIRED" ? INVOICE_EXPIRED_MSG : null);
        setPhase(PayPhase.CLOSED);
        setBanks([]);
      }
    };
    poll();
    const onVis = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVis);
    const id = setInterval(poll, POLL_MS);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [phase, invoiceId, onPaid]);

  const navigate = (link) => {
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "link", url: link }));
    } catch {
      window.location.href = link;
    }
  };

  const startFirstQr = () => openViaCreateQpay();

  if ((phase === PayPhase.LOADING || loading) && banks === null && phase !== PayPhase.CLOSED) {
    return (
      <div className="mt-2">
        <p className="mb-3 text-sm font-medium text-slate-700">Банкны аппликейшнээр төлөх</p>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col items-center rounded-xl border border-slate-100 bg-white p-3"
            >
              <Skeleton.Avatar active size={48} shape="square" />
              <Skeleton.Input active size="small" className="mt-2 !w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (phase === PayPhase.CLOSED) {
    return (
      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-700">
        {closedMessage || "Нэхэмжлэл төлөгдсөн эсвэл хаагдсан байна."}
      </div>
    );
  }

  if (phase === PayPhase.ERROR) {
    return (
      <div className="mt-2 rounded-xl border border-red-100 bg-red-50 p-4 text-center">
        <p className="mb-3 text-sm text-slate-700">Төлбөрийн холбоос авахад алдаа гарлаа</p>
        <Button
          type="primary"
          onClick={() => {
            fetchStartedRef.current = false;
            setBanks(null);
            openViaCreateQpay();
          }}
          className="!bg-smartTeal"
        >
          Дахин оролдох
        </Button>
      </div>
    );
  }

  if (phase === PayPhase.IDLE) {
    return (
      <div className="mt-2 rounded-xl border border-slate-100 bg-white p-4 text-center">
        <p className="mb-3 text-sm text-slate-700">QPay-ээр төлөх QR аваад банкны аппаар төлнө үү.</p>
        <Button type="primary" onClick={startFirstQr} className="!bg-smartTeal" loading={loading}>
          QR авах
        </Button>
      </div>
    );
  }

  const bankList = banks || [];
  const showTimer = phase === PayPhase.ACTIVE && expiresAtMs;

  return (
    <div className="mt-2 pb-6">
      {showTimer && (
        <div
          className={`invoice-pay-session-timer${
            remainingMs <= 60 * 1000 ? " invoice-pay-session-timer--urgent" : ""
          }`}
          role="status"
          aria-live="polite"
        >
          <span className="invoice-pay-session-timer__label">
            <ClockCircleOutlined style={{ marginRight: 6, color: "#2ea5a1" }} aria-hidden />
            Төлбөр хүлээн авах хугацаа
          </span>
          <span className="invoice-pay-session-timer__time">{formatMmSs(remainingMs)}</span>
          <span className="invoice-pay-session-timer__hint">{sessionTimerHint(remainingMs)}</span>
        </div>
      )}
      <p className="mb-3 text-sm font-medium text-slate-700">Банкны аппликейшнээр төлөх</p>
      {bankList.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">Банкны жагсаалт хоосон байна</p>
      ) : (
        <div className="grid max-h-[min(52vh,420px)] grid-cols-3 gap-2 overflow-y-auto pr-1 sm:gap-3">
          {bankList.map((bank, index) => (
            <button
              type="button"
              key={`${bank.name}-${index}`}
              onClick={() => navigate(bank.link)}
              className="flex min-h-[80px] flex-col items-center justify-center rounded-xl border border-slate-100 bg-white p-2 shadow-sm transition hover:border-smartCyan hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-smartCyan active:scale-[0.98] sm:min-h-[88px] sm:p-3"
            >
              <img
                src={bank.logo}
                alt={bank.name}
                className="h-10 w-10 rounded-lg object-contain sm:h-12 sm:w-12 sm:rounded-xl"
                width="48"
                height="48"
              />
              <span className="mt-2 line-clamp-2 text-center text-xs leading-tight text-slate-600">
                {bank.description || bank.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QpayBankList;
